'use client';
import React from 'react';
import { toast } from 'sonner';
import { MapPin, Award, Star, Globe, FileCheck, ShieldCheck, MessageSquare, Handshake } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';


type Product = {
  id: string;
  name: string;
  supplier: string;
  country: string;
  flag: string;
  category: string;
  certStatus: string;
  certBody: string;
  certId: string;
  trustScore: number;
  exportReady: string[];
  moq: string;
  description: string;
};

interface ProductDetailModalProps {
  product: Product;
  onClose: () => void;
}

export default function ProductDetailModal({ product, onClose }: ProductDetailModalProps) {
  const certBadgeVariant = (status: string): 'valid' | 'expiring' | 'review' | 'pending' => {
    if (status === 'Valid') return 'valid';
    if (status === 'Expiring Soon') return 'expiring';
    if (status === 'Under Review') return 'review';
    return 'pending';
  };

  return (
    <Modal open onClose={onClose} title="Product & Supplier Details" size="lg">
      <div className="space-y-6">
        {/* Header */}
        <div
          className="flex items-start gap-5 p-4 rounded-xl"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <div
            className="w-20 h-20 rounded-xl flex items-center justify-center text-4xl shrink-0"
            style={{ background: 'var(--card)' }}
          >
            {product.flag}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold mb-1">{product.name}</h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    <MapPin size={13} />
                    {product.supplier} · {product.country}
                  </div>
                </div>
              </div>
              <StatusBadge variant={certBadgeVariant(product.certStatus)} label={product.certStatus} size="md" />
            </div>
            <div className="flex items-center gap-4 mt-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Star size={14} style={{ color: 'var(--gold)' }} fill="var(--gold)" />
                <span className="text-sm font-semibold font-mono-data" style={{ color: 'var(--gold)' }}>{product.trustScore}% Trust Score</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                <Award size={13} style={{ color: 'var(--emerald-bright)' }} />
                {product.certBody}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <h4 className="text-sm font-semibold mb-2">Product Description</h4>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{product.description}</p>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Category', value: product.category, icon: Globe },
            { label: 'MOQ', value: product.moq, icon: FileCheck },
            { label: 'Certificate ID', value: product.certId, icon: Award, mono: true },
            { label: 'Certification Body', value: product.certBody, icon: ShieldCheck },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={`detail-${item.label}`}
                className="rounded-xl p-3"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={13} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-xs uppercase tracking-wide font-semibold" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.05em' }}>{item.label}</span>
                </div>
                <p className={`text-sm font-semibold ${item.mono ? 'font-mono-data' : ''}`} style={{ color: item.mono ? 'var(--emerald-bright)' : 'var(--foreground)' }}>
                  {item.value}
                </p>
              </div>
            );
          })}
        </div>

        {/* Export Markets */}
        <div>
          <h4 className="text-sm font-semibold mb-3">Export Ready Markets</h4>
          <div className="flex flex-wrap gap-2">
            {product.exportReady.map((market) => (
              <span
                key={`expmarket-${product.id}-${market}`}
                className="px-3 py-1.5 rounded-full text-sm font-medium"
                style={{ background: 'rgba(10,107,79,0.12)', color: 'var(--emerald-bright)', border: '1px solid rgba(10,107,79,0.25)' }}
              >
                {market}
              </span>
            ))}
          </div>
        </div>

        {/* Compliance Documents */}
        <div>
          <h4 className="text-sm font-semibold mb-3">Compliance Documents</h4>
          <div className="space-y-2">
            {[
              { label: 'Halal Certificate', status: 'Verified' },
              { label: 'Product Registration', status: 'Verified' },
              { label: 'Export Licence', status: product.certStatus === 'Under Review' ? 'Under Review' : 'Verified' },
              { label: 'Shariah Review Report', status: product.certStatus === 'Under Review' ? 'Pending' : 'Completed' },
            ].map((doc) => (
              <div
                key={`doc-${product.id}-${doc.label}`}
                className="flex items-center justify-between px-3 py-2.5 rounded-lg"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-center gap-2">
                  <FileCheck size={14} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-sm">{doc.label}</span>
                </div>
                <StatusBadge
                  variant={doc.status === 'Verified' || doc.status === 'Completed' ? 'valid' : doc.status === 'Under Review' ? 'review' : 'pending'}
                  label={doc.status}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => { toast.success('Investment pack requested'); onClose(); }}
            className="flex-1 btn-primary flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
          >
            <MessageSquare size={15} />
            Request Quotation
          </button>
          <button
            onClick={() => { toast.success('Business matching request submitted'); onClose(); }}
            className="flex-1 btn-outline flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
          >
            <Handshake size={15} />
            Request Business Matching
          </button>
        </div>
      </div>
    </Modal>
  );
}